// utils/currencyFormat.ts

/**
 * Groups thousands on the integer part of a numeric string.
 * Keeps any existing fractional part intact (e.g., "1234.56" -> "1,234.56").
 */
export const groupThousands = (numStr: string) => {
  if (!numStr) return '';
  const isNeg = numStr.startsWith('-');
  const cleaned = numStr.replace(/[^0-9.]/g, '').replace(/^0+(?=\d)/, ''); // keep dot
  const [intPart = '', fracPart] = cleaned.split('.');
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const joined =
    fracPart !== undefined ? `${withCommas}.${fracPart}` : withCommas;
  return isNeg ? `-${joined}` : joined;
};

/**
 * AntD InputNumber formatter for currencies.
 * - Preserves decimals user types
 * - Adds thousand separators
 * - Adds a currency prefix (default: "$ ")
 */
export const formatCurrencyForAntd = (
  value?: string | number,
  prefix: string = '$ '
): string => {
  if (value === undefined || value === null || value === '') return '';
  const str = String(value);
  // Allow users to type just "-" or end with "." without jumping the cursor
  if (str === '-' || str.endsWith('.')) {
    const base = groupThousands(str.replace(/[^0-9.-]/g, ''));
    return base ? `${prefix}${base}` : `${prefix}${str}`;
  }
  const grouped = groupThousands(str.replace(/[^0-9.-]/g, ''));
  return grouped ? `${prefix}${grouped}` : '';
};

/**
 * Matching AntD InputNumber parser for the above formatter.
 * Strips currency symbol, spaces and commas -> numeric string AntD can parse.
 */
export const parseCurrencyFromAntd = (value?: string): number => {
  if (!value) return 0;
  const numeric = value.replace(/[^0-9.-]/g, '');
  // Handle inputs like "-" or "." gracefully
  if (numeric === '' || numeric === '-' || numeric === '.') return 0;
  const n = Number(numeric);
  return Number.isNaN(n) ? 0 : n;
};
